import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/page-header';
import { setWebTitle, formatPrice } from '@/lib/format-utils';
import { useFinancesSummary, useSubscriptions } from '@/hooks/api-hooks';
import { SkeletonList } from '@/components/snippets';
import { Link } from 'react-router-dom';
import { SUBSCRIPTIONS_PATH } from '@/lib/navigation';
import { Badge, Card } from '@mantine/core';

export default function FinancesPage(): JSX.Element {
    const { t } = useTranslation();

    setWebTitle(t('finances'));

    const {
        data: financesData,
        isLoading
    } = useFinancesSummary();

    const {
        data: subscriptions,
        isLoading: isSubscriptionsLoading
    } = useSubscriptions();

    const totals = financesData?.data || {
        totalIncome: {},
        confirmedIncome: {},
        pendingIncome: {},
        totalEgress: {},
        confirmedEgress: {},
        pendingEgress: {},
    };

    const MoneyByCurrency = ({ money, className }: { money: Record<string, number>, className?: string }) => {
        if (!money || Object.keys(money).length === 0) {
            return <div className="text-lg font-semibold text-gray-400">{formatPrice(0)}</div>;
        }
        return (
            <div className="space-y-0.5">
                {Object.entries(money).map(([currency, amount]) => (
                    <div key={currency} className={className || ''}>
                        {formatPrice(amount)} <span className="text-gray-400">{currency}</span>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div>
            <PageHeader
                title={t('finances')}
            />

            <div className="panel-content">
                {isLoading && <SkeletonList />}

                {!isLoading && (
                    <div className="flex gap-6 flex-wrap">
                        {/* Reservations */}
                        <Card shadow="sm" radius="md" padding="xl" className="w-fit h-fit">
                            <Card.Section withBorder inheritPadding py="sm">
                                <div className="text-sm font-semibold uppercase tracking-wide">
                                    {t('reservations')}
                                </div>
                            </Card.Section>

                            <div className="space-y-5 pt-4">
                                <div className="flex items-start gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs font-semibold text-gray-400 uppercase mb-1">
                                            {t('incomes')}
                                        </div>
                                        <MoneyByCurrency money={totals.totalIncome} className="text-xl font-semibold text-gray-900" />
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs font-semibold text-gray-400 uppercase mb-1">
                                            {t('costs')}
                                        </div>
                                        <MoneyByCurrency money={totals.totalEgress} className="text-xl font-semibold text-gray-900" />
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Confirmed Reservations */}
                        <Card shadow="sm" radius="md" padding="xl" className="w-fit h-fit">
                            <Card.Section withBorder inheritPadding py="sm">
                                <div className="text-sm font-semibold uppercase tracking-wide">
                                    {t('reservations')} {t('confirmed')}
                                </div>
                            </Card.Section>

                            <div className="space-y-5 pt-4">
                                <div className="flex items-start gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs font-semibold text-gray-400 uppercase mb-1">
                                            {t('income')}
                                        </div>
                                        <MoneyByCurrency money={totals.confirmedIncome} className="text-xl font-semibold text-green-700" />
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs font-semibold text-gray-400 uppercase mb-1">
                                            {t('costs')}
                                        </div>
                                        <MoneyByCurrency money={totals.confirmedEgress} className="text-xl font-semibold text-gray-900" />
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Pending Reservations */}
                        <Card shadow="sm" radius="md" padding="xl" className="w-fit h-fit">
                            <Card.Section withBorder inheritPadding py="sm">
                                <div className="text-sm font-semibold uppercase tracking-wide">
                                    {t('reservations')} {t('pending')}
                                </div>
                            </Card.Section>

                            <div className="space-y-5 pt-4">
                                <div className="flex items-start gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs font-semibold text-gray-400 uppercase mb-1">
                                            {t('income')}
                                        </div>
                                        <MoneyByCurrency money={totals.pendingIncome} className="text-xl font-semibold text-orange-700" />
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs font-semibold text-gray-400 uppercase mb-1">
                                            {t('costs')}
                                        </div>
                                        <MoneyByCurrency money={totals.pendingEgress} className="text-xl font-semibold text-gray-900" />
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                )}

                {isLoading && <SkeletonList />}

                <div className="text-sm font-semibold mb-4 mt-10">
                    {t('recurring_expenses')}
                </div>
                <div className="flex flex-col border border-gray-200 rounded-md overflow-hidden bg-white">
                    <div className="flex items-center justify-between bg-gray-50 p-4 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        <div>{t('name')} / {t('cycle')}</div>
                        <div>{t('price')}</div>
                    </div>

                    {isSubscriptionsLoading && <SkeletonList />}
                    {subscriptions?.data?.map((subscription) => (
                        <Link
                            key={subscription.id}
                            to={`${SUBSCRIPTIONS_PATH}/${subscription.id}`}
                            className="block border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200 no-underline text-inherit"
                        >
                            <div className="flex items-center justify-between p-4">
                                <div className="flex-1">
                                    <div>{subscription.name}</div>
                                    <Badge color="blue" variant="light" size="xs" mt={4}>
                                        {t(`cycle_${subscription.cycle}`)}
                                    </Badge>
                                </div>
                                <div className="text-right">
                                    <div>{formatPrice(subscription.price)} {subscription.currency}</div>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>

        </div>
    );
}
